import React, { useRef } from "react";
import { useHistory } from "@core/routing/useHistory";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { usePostComments } from "@domain/post/hooks";
import { Post } from "@domain/post/post";
import { fullName } from "@domain/user/utils";
import { formatTimeAgo } from "@utils/date";
import { scrollToId } from "@utils/dom";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { Page } from "@ui/components/page";
import { CommentInput } from "@ui/components/post/comment/commentInput";
import { ParentCommentView } from "@ui/components/post/comment/parentCommentView";
import { PostContent } from "@ui/components/post/postContent";
import { PostMenu } from "@ui/components/post/postMenu";
import { ProfilePicture } from "@ui/components/profile/profilePicture";

interface PostPageMobileProps extends React.HTMLAttributes<HTMLDivElement> {
	post: Post;
}

export const PostPageMobile: React.FC<PostPageMobileProps> = ({ post, ...htmlProps }) => {
	const alias = useOrganizationAlias();
	const {
		result: comments,
		fetchMore,
		fetch,
		loading: commentsLoading,
		moreLoading,
	} = usePostComments(post.id, alias?.id);
	const history = useHistory();
	const commentRef = useRef<{ focus: () => void }>(null);

	const { author, creationDate } = post;
	return (
		<Container
			containerStyle={{ padding: 0, display: "flex", flexDirection: "column", minHeight: "100vh" }}
			backText=""
			{...htmlProps}
			header={
				<AuthorHeader>
					<Link to={Path.Profile(author)}>
						<HeaderLeft>
							<Picture picture={author.avatar} size={"M"} />
							<AuthorName>{fullName(author)}</AuthorName>
							<DateInfos>{formatTimeAgo(creationDate)}</DateInfos>
						</HeaderLeft>
					</Link>
					<KebabMenu closeOnSelect>
						<PostMenu post={post} onDelete={() => history.push(Path.Root)} />
					</KebabMenu>
				</AuthorHeader>
			}
		>
			<PageContent>
				<Content post={post} onCommentAction={() => commentRef.current?.focus()} />
				<Separator />
				<StyledCommentInput
					ref={commentRef}
					postId={post.id}
					onCommentSubmit={async (comment) => {
						await fetch();
						if (comment) {
							scrollToId(comment.id);
						}
					}}
				/>
				<InfiniteScroll onEndReached={fetchMore} loading={commentsLoading || moreLoading}>
					<Comments>
						{comments.map((comment) =>
							comment ? <ParentCommentView key={comment.id} postComment={comment} postId={post.id} /> : <></>
						)}
					</Comments>
				</InfiniteScroll>
			</PageContent>
		</Container>
	);
};

const Container = styled(Page)`
	> :not(:last-child) {
		margin-bottom: 20px;
	}
	display: flex;
	flex-direction: column;
	flex: 1;
`;

const AuthorHeader = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const HeaderLeft = styled.div`
	display: grid;
	column-gap: 10px;
	align-items: center;
	grid-template-areas:
		"picture display-name"
		"picture date";
`;

const Picture = styled(ProfilePicture)`
	grid-area: picture;
`;

const AuthorName = styled.div`
	grid-area: display-name;
	${({ theme }) => theme.textStyles.text};
`;

const DateInfos = styled.div`
	grid-area: date;
	${({ theme }) => theme.textStyles.tertiary};
	font-weight: 500;
`;

const PageContent = styled.div`
	background-color: ${({ theme }) => theme.colors.white};
	padding-top: 20px;
	flex: 1;
	border-top: solid 1px ${({ theme }) => theme.colors.lightergray};
`;

const Content = styled(PostContent)`
	padding: 20px 38px;
`;

const Separator = styled.div`
	height: 1px;
	background-color: ${({ theme }) => theme.colors.lightergray};
`;

const Comments = styled.div`
	padding: 20px;

	> * {
		margin-bottom: 10px;
	}
`;

const StyledCommentInput = styled(CommentInput)`
	border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};
	padding: 10px 20px;
`;
